'use client';

import { createContext, useContext, useEffect, useState } from 'react';

interface UserIdContextValue {
  userId: string | null;
}

const UserIdContext = createContext<UserIdContextValue>({ userId: null });

/**
 * Provides the current user ID to the component tree.
 *
 * When the server provides a userId (online path), it is cached to IDB
 * so that offline app loads can fall back to the cached value even if
 * the auth cookie has expired.
 */
export function UserIdProvider({
  userId: serverUserId,
  children,
}: {
  userId: string | null;
  children: React.ReactNode;
}) {
  const [userId, setUserId] = useState<string | null>(serverUserId);

  useEffect(() => {
    if (serverUserId) {
      void import('@/lib/persistence/db').then(({ setCachedUserId }) => {
        setCachedUserId(serverUserId).catch(() => {});
      });
      setUserId(serverUserId);
    } else {
      let cancelled = false;
      void import('@/lib/persistence/db').then(({ getCachedUserId }) => {
        getCachedUserId()
          .then((cached) => {
            if (!cancelled && cached) {
              setUserId(cached);
            }
          })
          .catch(() => {});
      });
      return () => {
        cancelled = true;
      };
    }
  }, [serverUserId]);

  return <UserIdContext.Provider value={{ userId }}>{children}</UserIdContext.Provider>;
}

export function useUserIdContext(): string | null {
  const context = useContext(UserIdContext);
  return context.userId;
}
